/**
 * SellerPortalShell — persistent sidebar + mobile hamburger drawer layout for
 * the seller portal (/sellers/me/*). Mirrors AppShell's Framer AnimatePresence
 * drawer pattern and AccountLayout's NavLink isActive idiom.
 *
 * Desktop (≥md): fixed-width left sidebar with nav rail.
 * Mobile (<md): full-width content area with a sticky top bar; a slide-in
 *   drawer opens on hamburger tap (AnimatePresence + motion).
 *
 * Renders <Outlet /> for routed children (DashboardHome, ProductListPage,
 * SellerSettingsPage — wired in App.tsx by Task 6.1).
 */
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Icon } from "@garden/ui";

import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { logout } from "@/features/settings/api";
import { registerSellerPortalI18n, SELLER_PORTAL_NS } from "@/features/seller-portal/i18n";

const NAV = [
  { to: "/sellers/me", key: "home", testid: "seller-nav-home", end: true },
  { to: "/sellers/me/products", key: "products", testid: "seller-nav-products", end: false },
  { to: "/sellers/me/settings", key: "settings", testid: "seller-nav-settings", end: false },
] as const;

function navClass({ isActive }: { isActive: boolean }): string {
  return [
    "flex min-h-[44px] items-center rounded-[var(--g-radius)] px-3 py-2 text-sm font-medium transition-colors",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--g-surface)]",
    isActive
      ? "bg-[var(--g-primary-soft)] text-[var(--g-primary-ink)]"
      : "text-[var(--g-ink-2)] hover:bg-[var(--g-ground)] hover:text-[var(--g-ink)]",
  ].join(" ");
}

export function SellerPortalShell(): ReactElement {
  // Register the seller-portal i18n namespace on render — mirrors AccountLayout's
  // approach to avoid raw-key flash when the component mounts before initI18n races.
  registerSellerPortalI18n();
  const { t } = useTranslation(SELLER_PORTAL_NS);
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const navItems = NAV.map((item) => (
    <NavLink
      key={item.key}
      to={item.to}
      end={item.end}
      data-testid={item.testid}
      className={navClass}
      onClick={() => setDrawerOpen(false)}
    >
      {t(`nav.${item.key}`)}
    </NavLink>
  ));

  // Reuses the shared logout() (POST /auth/logout + clearAuth + clear the
  // TanStack cache — SCRUM-138), then routes to the landing page. Rendered at
  // the foot of both the desktop sidebar and the mobile drawer.
  const onLogout = async (): Promise<void> => {
    setDrawerOpen(false);
    await logout();
    navigate("/", { replace: true });
  };

  const logoutButton = (
    <button
      type="button"
      data-testid="seller-nav-logout"
      onClick={() => void onLogout()}
      className="mt-1 flex min-h-[44px] items-center gap-2 rounded-[var(--g-radius)] px-3 py-2 text-sm font-medium text-[var(--g-ink-2)] transition-colors hover:bg-[var(--g-ground)] hover:text-[var(--g-ink)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--g-surface)]"
    >
      <Icon name="logout" size={18} />
      {t("nav.logout")}
    </button>
  );

  return (
    <div className="flex min-h-screen w-full bg-[var(--g-ground)]">
      {/* ── Desktop sidebar (hidden below md) ── */}
      {/* Sticky + h-screen, NOT a plain flex child. As a plain child the aside
          stretched to the flex row's height — which is `<main>`'s height — so on
          the Products page it grew with the product list and `mt-auto` pushed
          "Log out" to the bottom of the PAGE. Reaching it meant scrolling past
          every product (SCRUM-192). `overflow-y-auto` keeps a long nav
          scrollable inside the pinned box rather than clipped. */}
      <aside
        data-testid="seller-sidebar"
        className="sticky top-0 hidden h-screen w-60 shrink-0 flex-col gap-1 overflow-y-auto border-r border-[var(--g-hairline)] bg-[var(--g-surface)] p-4 md:flex"
      >
        <p className="px-3 pb-2 text-xs font-semibold uppercase tracking-wide text-[var(--g-ink-3)]">
          {t("home.title")}
        </p>
        <nav aria-label={t("home.title")}>{navItems}</nav>
        <div className="mt-auto flex flex-col gap-2 border-t border-[var(--g-hairline)] pt-2">
          {/* The portal was the only one of the three shells without this —
              the RU/KY/EN bundles were already registered, nothing rendered
              the control (SCRUM-234). */}
          <LanguageSwitcher className="self-start" />
          {logoutButton}
        </div>
      </aside>

      {/* ── Mobile top bar with hamburger (hidden at md+) ── */}
      <header
        data-testid="seller-mobile-topbar"
        className="fixed inset-x-0 top-0 z-20 flex h-12 items-center gap-2 border-b border-[var(--g-hairline)] bg-[var(--g-surface)] px-3 md:hidden"
      >
        <button
          type="button"
          aria-label={t("nav.menu")}
          aria-expanded={drawerOpen}
          data-testid="seller-drawer-open"
          onClick={() => setDrawerOpen(true)}
          className="flex size-9 items-center justify-center rounded-[var(--g-radius)] text-[var(--g-ink-2)] hover:bg-[var(--g-ground)]"
        >
          <Icon name="menu" size={20} />
        </button>
        <span className="text-sm font-semibold text-[var(--g-ink)]">{t("home.title")}</span>
        {/* `ml-auto` parks it opposite the hamburger. Mobile matters most here:
            this product is used one-handed in a bazaar (SCRUM-234). */}
        <LanguageSwitcher className="ml-auto" />
      </header>

      {/* ── Mobile slide-in drawer ── */}
      <AnimatePresence>
        {drawerOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              className="fixed inset-0 z-30 bg-black/40 md:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              onClick={() => setDrawerOpen(false)}
              data-testid="seller-drawer-backdrop"
              aria-hidden="true"
            />

            {/* Drawer panel */}
            <motion.aside
              role="dialog"
              aria-modal="true"
              aria-label={t("home.title")}
              className="fixed inset-y-0 left-0 z-40 flex w-64 flex-col gap-1 bg-[var(--g-surface)] p-4 shadow-lg md:hidden"
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "tween", duration: 0.2 }}
            >
              {/* Close button */}
              <button
                type="button"
                aria-label={t("nav.close")}
                onClick={() => setDrawerOpen(false)}
                className="mb-2 self-end flex size-9 items-center justify-center rounded-[var(--g-radius)] text-[var(--g-ink-2)] hover:bg-[var(--g-ground)]"
              >
                <Icon name="x" size={20} />
              </button>

              <nav aria-label={t("home.title")}>{navItems}</nav>
              <div className="mt-auto border-t border-[var(--g-hairline)] pt-2">
                {logoutButton}
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* ── Routed content ── */}
      <main className="flex-1 px-4 pb-10 pt-16 md:pt-6">
        <Outlet />
      </main>
    </div>
  );
}

export default SellerPortalShell;
